import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Copy, Save, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export function AdminProjects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit({ title: "", excerpt: "", location: "", category: "residential", style: "", year: new Date().getFullYear(), services: [], client_brief: "", concept: "", challenges: "", materials: "", cover_image: "", gallery: [], featured: false, status: "draft", seo_title: "", seo_description: "" }); setOpen(true); };
  const openEdit = (p) => { setEdit({ ...p }); setOpen(true); };

  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/projects/${edit.id}`, edit);
      else await api.post(`/admin/projects`, edit);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.title}?`)) return; await api.delete(`/admin/projects/${p.id}`); toast.success("Deleted"); load(); };
  const duplicate = async (p) => { await api.post(`/admin/projects/${p.id}/duplicate`); toast.success("Duplicated"); load(); };

  return (
    <div data-testid="page-admin-projects" className="max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div><div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-2">Content</div><h1 className="font-serif text-4xl tracking-tight">Projects</h1></div>
        <button onClick={openNew} data-testid="project-new" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A85C42] text-white text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-[#2B2927]"><Plus size={14} /> New project</button>
      </div>
      <div className="bg-white border border-[#D5D0C8] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#EAE6DF] text-xs uppercase tracking-[0.2em] text-[#7A736C]"><tr><th className="text-left px-4 py-3">Project</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Year</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Views</th><th></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-t border-[#EAE6DF]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.cover_image && <img src={p.cover_image} alt="" className="w-10 h-10 object-cover rounded-sm" />}
                    <div><div className="font-medium">{p.title}</div><div className="text-xs text-[#7A736C]">{p.location}</div></div>
                  </div>
                </td>
                <td className="px-4 py-3">{p.category}</td>
                <td className="px-4 py-3">{p.year || "—"}</td>
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-[0.2em] text-[#A85C42]">{p.status}{p.featured ? " · ★" : ""}</span></td>
                <td className="px-4 py-3">{p.views || 0}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => openEdit(p)} data-testid={`project-edit-${p.id}`} className="text-xs underline underline-offset-4 hover:text-[#A85C42]">Edit</button>
                  <button onClick={() => duplicate(p)} className="text-[#7A736C] hover:text-[#A85C42]" title="Duplicate"><Copy size={14} /></button>
                  <button onClick={() => del(p)} className="text-[#7A736C] hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
          {edit && <ProjectForm value={edit} onChange={setEdit} />}
          <DialogFooter>
            <button onClick={save} data-testid="project-save" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A85C42] text-white text-xs uppercase tracking-[0.2em] rounded-sm"><Save size={14} /> Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectForm({ value, onChange }) {
  const v = value;
  const up = (k, val) => onChange({ ...v, [k]: val });
  const addGallery = () => up("gallery", [...(v.gallery || []), { url: "", alt: "" }]);
  const upGallery = (i, k, val) => { const g = [...(v.gallery || [])]; g[i] = { ...g[i], [k]: val }; up("gallery", g); };
  const rmGallery = (i) => up("gallery", v.gallery.filter((_, ii) => ii !== i));
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <F label="Title"><input className="input" value={v.title} onChange={(e) => up("title", e.target.value)} /></F>
        <F label="Slug (auto if blank)"><input className="input" value={v.slug || ""} onChange={(e) => up("slug", e.target.value)} /></F>
        <F label="Location"><input className="input" value={v.location} onChange={(e) => up("location", e.target.value)} /></F>
        <F label="Year"><input type="number" className="input" value={v.year || ""} onChange={(e) => up("year", parseInt(e.target.value) || null)} /></F>
        <F label="Category"><select className="input" value={v.category} onChange={(e) => up("category", e.target.value)}><option value="residential">Residential</option><option value="commercial">Commercial</option></select></F>
        <F label="Style"><input className="input" value={v.style} onChange={(e) => up("style", e.target.value)} /></F>
        <F label="Cover image URL"><input className="input" value={v.cover_image} onChange={(e) => up("cover_image", e.target.value)} /></F>
        <F label="Services (comma)"><input className="input" value={(v.services || []).join(", ")} onChange={(e) => up("services", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></F>
      </div>
      <F label="Excerpt"><textarea rows={2} className="input" value={v.excerpt} onChange={(e) => up("excerpt", e.target.value)} /></F>
      <F label="Client brief"><textarea rows={3} className="input" value={v.client_brief} onChange={(e) => up("client_brief", e.target.value)} /></F>
      <F label="Design concept"><textarea rows={3} className="input" value={v.concept} onChange={(e) => up("concept", e.target.value)} /></F>
      <F label="Challenges"><textarea rows={2} className="input" value={v.challenges} onChange={(e) => up("challenges", e.target.value)} /></F>
      <F label="Materials & palette"><textarea rows={2} className="input" value={v.materials} onChange={(e) => up("materials", e.target.value)} /></F>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#7A736C] mb-2 flex items-center justify-between">Gallery <button onClick={addGallery} className="text-xs underline hover:text-[#A85C42]">+ Add image</button></div>
        <div className="space-y-2">
          {(v.gallery || []).map((g, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className="input flex-1" placeholder="Image URL" value={g.url} onChange={(e) => upGallery(i, "url", e.target.value)} />
              <input className="input flex-1" placeholder="Alt text" value={g.alt || ""} onChange={(e) => upGallery(i, "alt", e.target.value)} />
              <button onClick={() => rmGallery(i)} className="text-red-600 p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Instagram URL"><input className="input" value={v.instagram_url || ""} onChange={(e) => up("instagram_url", e.target.value)} /></F>
        <F label="Video URL"><input className="input" value={v.video_url || ""} onChange={(e) => up("video_url", e.target.value)} /></F>
        <F label="SEO title"><input className="input" value={v.seo_title || ""} onChange={(e) => up("seo_title", e.target.value)} /></F>
        <F label="SEO description"><input className="input" value={v.seo_description || ""} onChange={(e) => up("seo_description", e.target.value)} /></F>
        <F label="Status"><select className="input" value={v.status} onChange={(e) => up("status", e.target.value)}><option>draft</option><option>published</option><option>archived</option></select></F>
        <F label="Featured"><select className="input" value={v.featured ? "yes" : "no"} onChange={(e) => up("featured", e.target.value === "yes")}><option value="no">No</option><option value="yes">Yes</option></select></F>
      </div>
      <style>{`.input{background:white;border:1px solid #D5D0C8;padding:8px 10px;width:100%;font-size:13px;border-radius:3px;outline:none}.input:focus{border-color:#A85C42}`}</style>
    </div>
  );
}
function F({ label, children }) { return <label className="block"><span className="block text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-1">{label}</span>{children}</label>; }
